import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Chip,
  LinearProgress,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArticleIcon from '@mui/icons-material/Article';
import CampaignIcon from '@mui/icons-material/Campaign';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RefreshIcon from '@mui/icons-material/Refresh';

const STRATEGY_ENGINE_URL = process.env.REACT_APP_STRATEGY_ENGINE_URL || 'https://strategy-engine.houman-ghavamzadeh.workers.dev';

function stringToColor(string) {
  let hash = 0;
  for (let i = 0; i < string.length; i++) { hash = string.charCodeAt(i) + ((hash << 5) - hash); }
  let color = '#';
  for (let i = 0; i < 3; i++) { color += `00${((hash >> (i * 8)) & 0xff).toString(16)}`.slice(-2); }
  return color;
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((w) => w[0]).join('').substring(0, 2).toUpperCase();
}

const planColors = { starter: '#3b82f6', professional: '#00FF94', enterprise: '#A855F7' };

function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
  };

  const fetchStats = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${STRATEGY_ENGINE_URL}/api/admin/stats`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      } else {
        setError(data.error || 'Failed to load stats');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const totalUsers = stats?.totalUsers || 0;
  const planDist = stats?.planDistribution || [];
  const recentUsers = stats?.recentUsers || [];

  const planDistWithPct = planDist.map(p => ({
    ...p,
    name: p.planId || 'unknown',
    percentage: totalUsers > 0 ? Math.round((p.count / totalUsers) * 100) : 0,
    color: planColors[p.planId] || '#6B7280',
  }));

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Typography variant="h5" gutterBottom>Loading Dashboard...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Admin Dashboard</Typography>
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={fetchStats}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Main Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PeopleIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Total Users</Typography>
            </Box>
            <Typography variant="h4">{stats?.totalUsers || 0}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {stats?.activeUsers || 0} active
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountTreeIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Total Strategies</Typography>
            </Box>
            <Typography variant="h4">{stats?.totalStrategies || 0}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Across all users
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PlayArrowIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Active Runs</Typography>
            </Box>
            <Typography variant="h4">{stats?.activeRuns || 0}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {stats?.totalRuns || 0} total runs
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ShowChartIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">System Health</Typography>
            </Box>
            <Typography variant="h4" color="success.main">OK</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              All services running
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Plan Distribution */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Plan Distribution</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            {planDistWithPct.length > 0 && (
              <>
                <Box sx={{ display: 'flex', height: 25, mb: 2 }}>
                  {planDistWithPct.map((plan) => (
                    <Box key={plan.name} sx={{ width: `${Math.max(plan.percentage, 2)}%`, bgcolor: plan.color, borderRadius: 0.5, mr: 0.25 }} />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {planDistWithPct.map((plan) => (
                    <Box key={plan.name} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: plan.color, mr: 1 }} />
                      <Typography variant="body2">
                        {plan.name}: {plan.percentage}% ({plan.count} users)
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>Users by Plan</Typography>
            {planDistWithPct.map((plan) => (
              <Box key={plan.name} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{plan.name}</Typography>
                <Typography variant="body2" fontWeight="bold">{plan.count} users</Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Total Users</Typography>
              <Typography variant="body2" fontWeight="bold">{totalUsers}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Recent Users */}
      <Paper sx={{ mb: 4, p: 3 }}>
        <Typography variant="h6" gutterBottom>Recent Users</Typography>
        {recentUsers.length > 0 ? (
          <List disablePadding>
            {recentUsers.map((user) => (
              <ListItem
                key={user.id}
                secondaryAction={<IconButton edge="end"><MoreVertIcon /></IconButton>}
                divider
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: stringToColor(user.displayName || user.email), width: 36, height: 36, fontSize: '0.85rem' }}>
                    {getInitials(user.displayName || user.email)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.displayName || user.email}
                  secondary={`${user.email} • Joined ${user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}`}
                />
                <Chip
                  label={user.planId || 'starter'}
                  size="small"
                  sx={{ mr: 1, textTransform: 'capitalize' }}
                  color={user.planId === 'professional' ? 'primary' : user.planId === 'enterprise' ? 'secondary' : 'default'}
                />
                <Chip
                  label={user.status}
                  size="small"
                  color={user.status === 'active' ? 'success' : 'default'}
                  sx={{ mr: 2 }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography color="text.secondary">No users yet</Typography>
        )}
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button onClick={() => navigate('/admin/users')}>View All Users</Button>
        </Box>
      </Paper>

      {/* Quick Access Cards */}
      <Typography variant="h6" gutterBottom>Quick Access</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/admin/users')}>
            <CardContent><PeopleIcon color="primary" sx={{ fontSize: 40, mb: 1 }} /><Typography variant="subtitle1">Users</Typography></CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/admin/plans')}>
            <CardContent><AttachMoneyIcon color="primary" sx={{ fontSize: 40, mb: 1 }} /><Typography variant="subtitle1">Plans</Typography></CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/admin/tickets')}>
            <CardContent><SupportAgentIcon color="primary" sx={{ fontSize: 40, mb: 1 }} /><Typography variant="subtitle1">Support</Typography></CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/admin/payments')}>
            <CardContent><ReceiptIcon color="primary" sx={{ fontSize: 40, mb: 1 }} /><Typography variant="subtitle1">Billing</Typography></CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/admin/blog')}>
            <CardContent><ArticleIcon color="primary" sx={{ fontSize: 40, mb: 1 }} /><Typography variant="subtitle1">Blog</Typography></CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/admin/marketing')}>
            <CardContent><CampaignIcon color="primary" sx={{ fontSize: 40, mb: 1 }} /><Typography variant="subtitle1">Marketing</Typography></CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AdminDashboard;
